import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect, useRef, useState } from 'react';
import { SectionId } from '~/constants/vdp/section';
import GlobalContext from '~/context/vdp/GlobalContext';
import { SectionReadinessStateContext } from '~/context/vdp/SectionReadinessContext';
import { useIsNewVehicle } from '~/context/vdp/VehicleContext';
import scrollToElement from '~/utilities/vdp/scrollToElement';

export const SCROLL_TIMEOUT_MS = 5000;

function getScrollToParam(): string {
  const params = new URLSearchParams(window.location.search);

  return params.get('scrollTo') ?? '';
}

function removeScrollToParam() {
  const url = new URL(window.location.href);
  url.searchParams.delete('scrollTo');
  window.history.replaceState(null, '', url.pathname + url.search);
}

/** Waits for smooth-scroll to settle, then reports drift between target and resting position to DD. */
function measureScrollAccuracy(
  intendedTop: number,
  sectionId: string,
  waitDuration: number
) {
  let maxTimeout: number;
  let settleTimeout: number;

  function onScrollEnd() {
    clearTimeout(settleTimeout);
    settleTimeout = window.setTimeout(() => {
      clearTimeout(maxTimeout);
      window.removeEventListener('scroll', onScrollEnd);

      const actualTop = window.scrollY;
      const driftPixels = Math.round(intendedTop - actualTop);

      datadogRum.addAction('vdp-scrollto-accuracy', {
        actualTop: Math.round(actualTop),
        driftPixels,
        intendedTop: Math.round(intendedTop),
        sectionId,
        waitDuration
      });
    }, 150);
  }

  window.addEventListener('scroll', onScrollEnd, { passive: true });

  maxTimeout = window.setTimeout(() => {
    clearTimeout(settleTimeout);
    window.removeEventListener('scroll', onScrollEnd);
  }, 3000);

  onScrollEnd();

  return () => {
    clearTimeout(maxTimeout);
    clearTimeout(settleTimeout);
    window.removeEventListener('scroll', onScrollEnd);
  };
}

// DD actions: vdp-scrollto-{detected,completed,cancelled,timeout,accuracy}
export default function useScrollToParam() {
  const { setLazyLoadEnabled } = useContext(GlobalContext);
  const isNewCar = useIsNewVehicle();
  const { readyStates, registeredSections } = useContext(
    SectionReadinessStateContext
  );
  const [hash, setHash] = useState('');

  const accuracyCleanupRef = useRef<(() => void) | null>(null);
  const readyStatesRef = useRef(readyStates);
  const scrollTargetRef = useRef('');
  const shouldScrollRef = useRef(false);
  const startTimeRef = useRef(0);
  const timeoutRef = useRef(0);
  readyStatesRef.current = readyStates;

  // On mount: capture scrollTo query param and prepare for scroll.
  // Defer removeScrollToParam() so the value survives StrictMode double-mount.
  useEffect(() => {
    const target = getScrollToParam();
    if (!target) return;

    scrollTargetRef.current = target;
    shouldScrollRef.current = true;
    startTimeRef.current = performance.now();
    setLazyLoadEnabled(false);

    datadogRum.addAction('vdp-scrollto-detected', { isNewCar, target });

    const cancelScrollAttempt = () => {
      if (!shouldScrollRef.current) return;

      shouldScrollRef.current = false;
      clearTimeout(timeoutRef.current);
      removeScrollToParam();
      datadogRum.addAction('vdp-scrollto-cancelled', {
        isNewCar,
        target,
        waitDuration: performance.now() - startTimeRef.current
      });
    };

    window.addEventListener('wheel', cancelScrollAttempt, { passive: true });
    window.addEventListener('touchmove', cancelScrollAttempt, {
      passive: true
    });

    timeoutRef.current = window.setTimeout(() => {
      shouldScrollRef.current = false;
      removeScrollToParam();
      datadogRum.addAction('vdp-scrollto-timeout', {
        isNewCar,
        readyStates: { ...readyStatesRef.current },
        registeredSections: [...registeredSections],
        target
      });
    }, SCROLL_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('wheel', cancelScrollAttempt);
      window.removeEventListener('touchmove', cancelScrollAttempt);
      accuracyCleanupRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only effect; registeredSections is a ref-backed Set read at fire time
  }, [setLazyLoadEnabled]);

  // When the target and all registered sections are ready, scroll once
  useEffect(() => {
    const target = scrollTargetRef.current;
    if (!target || !shouldScrollRef.current) return;
    if (!registeredSections.has(target)) return;

    const registered = [...registeredSections];
    const notReady = registered.filter(id => !readyStates[id]);
    if (notReady.length > 0) return;

    shouldScrollRef.current = false;
    clearTimeout(timeoutRef.current);
    removeScrollToParam();

    const element = document.getElementById(target);
    const computedStyle = element
      ? window.getComputedStyle(element)
      : undefined;
    const scrollMarginTop =
      parseInt(computedStyle?.scrollMarginTop ?? '0', 10) || 0;
    const intendedTop = element
      ? element.getBoundingClientRect().top + window.scrollY - scrollMarginTop
      : 0;

    scrollToElement(target);

    const waitDuration = performance.now() - startTimeRef.current;

    datadogRum.addAction('vdp-scrollto-completed', {
      isNewCar,
      registeredSections: registered,
      target,
      waitDuration
    });

    accuracyCleanupRef.current = measureScrollAccuracy(
      intendedTop,
      target,
      waitDuration
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- registeredSections is a ref-backed Set, stable across renders
  }, [readyStates]);

  // Sync hash state on mount and hashchange (for mid-page navigation)
  useEffect(() => {
    const setPageHash = () => {
      setHash(window.location.hash.substring(1));
    };

    setPageHash();

    window.addEventListener('hashchange', setPageHash, false);

    return () => {
      window.removeEventListener('hashchange', setPageHash, false);
    };
  }, []);

  // Disable lazy loading when hash matches a known below-fold section
  useEffect(() => {
    if (registeredSections.has(hash)) {
      setLazyLoadEnabled(false);
    } else if (hash === SectionId.Root) {
      window.history.replaceState(
        '',
        document.title,
        window.location.pathname + window.location.search
      );
    }
  }, [hash, registeredSections, setLazyLoadEnabled]);
}
